/**
 * Firebase — the single place the SDK is initialised.
 *
 * Everything is imported from the MODULAR (`firebase/*`) entry points so the
 * bundler can tree-shake. Never `import firebase from 'firebase'`.
 *
 * Exports typed singletons. Import these, never call initializeApp again.
 *
 * ── FIRESTORE AND STORAGE ARE NOT HERE ──────────────────────────────────────
 * They live in `./firestore` and `./bucket`, one instance each, for one
 * reason: this module is EAGER — the ballot needs `app` and `auth` — and a
 * static `import { getFirestore } from 'firebase/firestore'` here drags the
 * whole Firestore SDK into the entry chunk, the one chunk a voter on a
 * mid-range Android phone waits on before seeing anything at all. Firestore is
 * about a third of it. A module already inside a lazy chunk imports those two
 * normally; a module on the ballot's eager path must `await import()` them.
 */

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import type { Firestore } from 'firebase/firestore';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';

export interface MonroeFirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

function readEnv(key: string): string {
  const value = import.meta.env[key as keyof ImportMetaEnv];
  return typeof value === 'string' ? value : '';
}

/**
 * The project's public web config, read from VITE_* env vars.
 *
 * A Firebase web API key is public by design — it identifies the project and
 * authorises nothing. Access control lives entirely in firestore.rules and
 * storage.rules. Shipping it in the bundle is expected.
 */
export const firebaseConfig: MonroeFirebaseConfig = {
  apiKey: readEnv('VITE_FIREBASE_API_KEY'),
  authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN') || 'projectmonroe-sz.firebaseapp.com',
  projectId: readEnv('VITE_FIREBASE_PROJECT_ID') || 'projectmonroe-sz',
  storageBucket:
    readEnv('VITE_FIREBASE_STORAGE_BUCKET') || 'projectmonroe-sz.firebasestorage.app',
  messagingSenderId: readEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: readEnv('VITE_FIREBASE_APP_ID'),
  ...(readEnv('VITE_FIREBASE_MEASUREMENT_ID')
    ? { measurementId: readEnv('VITE_FIREBASE_MEASUREMENT_ID') }
    : {}),
};

/** True when the env file has not been filled in yet. Surface, don't crash. */
export const isFirebaseConfigured: boolean =
  firebaseConfig.apiKey.length > 0 && firebaseConfig.appId.length > 0;

export const EMULATOR_HOST = '127.0.0.1';

/** Ports mirror firebase.json. Changing one means changing both. */
export const EMULATOR_PORTS = {
  auth: 9099,
  firestore: 8080,
  storage: 9199,
  functions: 5001,
} as const;

/**
 * Callables live next to Firestore and the bucket. getFunctions() defaults to
 * us-central1, and a call to the wrong region fails as `not-found` — which
 * reads exactly like "never deployed". Pass the region, always.
 *
 * Must match functions/src/core/constants.ts REGION.
 */
export const FUNCTIONS_REGION = 'asia-south1';

/**
 * Emulators are used in dev unless explicitly turned off.
 * `import.meta.env.DEV` is statically replaced at build time, so the whole
 * emulator branch is dead-code-eliminated from a production bundle.
 */
export const useEmulators: boolean =
  import.meta.env.DEV &&
  readEnv('VITE_USE_EMULATORS') !== 'false' &&
  // Browser only. Unit tests import this module transitively (storage.ts pulls
  // in the `storage` singleton), and connecting emulators from a Node test
  // runner is pointless noise at best and a hung socket at worst.
  typeof window !== 'undefined';

export const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
export const functions: Functions = getFunctions(app, FUNCTIONS_REGION);

let dbPromise: Promise<Firestore> | undefined;

/**
 * Firestore for a module on the BALLOT's eager path, which must not import
 * `./firestore` statically — doing so is what puts the SDK in the entry chunk.
 * Memoised, and `getFirestore(app)` is idempotent, so this and `./firestore`
 * hand out the same instance whichever runs first.
 */
export function getDb(): Promise<Firestore> {
  dbPromise ??= import('firebase/firestore').then(({ getFirestore, connectFirestoreEmulator }) => {
    const instance = getFirestore(app);
    connectOnce('firestore', () =>
      connectFirestoreEmulator(instance, EMULATOR_HOST, EMULATOR_PORTS.firestore),
    );
    return instance;
  });
  return dbPromise;
}

/**
 * Connect to the local emulator suite.
 *
 * Auth and Functions are wired at module load; Firestore and Storage are wired
 * inside their getters, because that is the first moment their SDK exists.
 * Vite may evaluate a module twice under HMR, and connecting twice throws —
 * hence flags on globalThis rather than module-local booleans (a module-local
 * one is reset by the very reload we are guarding against).
 */
declare global {
  var __MONROE_EMULATORS_CONNECTED__: boolean | undefined;
  var __MONROE_FIRESTORE_EMULATOR__: boolean | undefined;
  var __MONROE_STORAGE_EMULATOR__: boolean | undefined;
}

/** Wire an emulator exactly once per page, whatever HMR does. See ./firestore. */
export function connectOnce(flag: 'firestore' | 'storage', connect: () => void): void {
  if (!useEmulators) return;
  const key = flag === 'firestore' ? '__MONROE_FIRESTORE_EMULATOR__' : '__MONROE_STORAGE_EMULATOR__';
  if (globalThis[key]) return;
  globalThis[key] = true;
  connect();
}

if (useEmulators && !globalThis.__MONROE_EMULATORS_CONNECTED__) {
  globalThis.__MONROE_EMULATORS_CONNECTED__ = true;
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
    disableWarnings: true,
  });
  connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);

  console.info(
    `[monroe] Firebase emulators connected (auth:${EMULATOR_PORTS.auth} firestore:${EMULATOR_PORTS.firestore} storage:${EMULATOR_PORTS.storage} functions:${EMULATOR_PORTS.functions})`,
  );
}
